import { CheckCheck, Settings, X } from 'lucide-react';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { Drawer } from 'vaul';

import { Heading } from '@/components/heading';
import { UserNotificationsList } from '@/components/modules/user-notifications/list';
import { Tooltip } from '@/components/tooltip';
import { DrawerAlt, DrawerAltOverlay, DrawerAltPortal } from '@/components/ui/drawer-alt';
import { LoadingIndicator } from '@/components/ui/loading/indicator';
import { VisuallyHidden } from '@/components/visually-hidden';
import { useWindowSize } from '@/hooks/use-window-size';
import { USER_NOTIFICATION_SETTINGS_URL } from '@/lib/constants';
import {
  userNotificationsErrorState,
  userNotificationsIsLoadingState,
  userNotificationsLoadMoreState,
  userNotificationsMarkAllAsReadState,
  userNotificationsMarkAsReadState,
  userNotificationsState,
} from '@/state/user-notifications';
import { BREAKPOINTS_AS_NUMBERS } from '@/theme/constants/breakpoints';
import { logEvent } from '@/utils/logger';
import { tv } from '@/utils/styles';

export interface AppNavigationNotificationsProps {
  isOpen: boolean;
  onOpenChange: (value: boolean) => void;
  offset: string;
}

const AppNavigationNotificationsDrawer = ({ offset, isOpen, onOpenChange }: AppNavigationNotificationsProps) => {
  const { width } = useWindowSize();
  const router = useRouter();
  const [isMarkingAllAsRead, setIsMarkingAllAsRead] = useState(false);
  const { notifications, count } = useRecoilValue(userNotificationsState);
  const error = useRecoilValue(userNotificationsErrorState);
  const loadMore = useRecoilValue(userNotificationsLoadMoreState);
  const isLoading = useRecoilValue(userNotificationsIsLoadingState);
  const markAsRead = useRecoilValue(userNotificationsMarkAsReadState);
  const markAllAsRead = useRecoilValue(userNotificationsMarkAllAsReadState);

  const hasUnread = !!(notifications?.filter((n) => !n.readAt)?.length || 0);
  const isSmallScreen = width! < BREAKPOINTS_AS_NUMBERS.MD;

  // This workaround is required to keep the app nav pointer interactions
  // working when the drawer is open. Even when noBodyStyles is passed to
  // the drawer it still disables pointer events. See discussion here
  // https://github.com/radix-ui/primitives/issues/2122
  useEffect(() => {
    if (isOpen) {
      // Pushing the change to the end of the call stack
      const timer = setTimeout(() => {
        document.body.style.pointerEvents = '';
      }, 0);

      return () => clearTimeout(timer);
    }
    document.body.style.pointerEvents = 'auto';
    return undefined;
  }, [isOpen]);

  const handleMarkAsRead = (id: string, isRead: boolean) => {
    markAsRead(id, isRead);
  };

  const handleMarkAllAsRead = async () => {
    setIsMarkingAllAsRead(true);
    try {
      await markAllAsRead();
    } catch (e: any) {
      logEvent.error('Error marking all notifications as read', e);
    } finally {
      setIsMarkingAllAsRead(false);
    }
  };

  const handleClickSettings = () => {
    router.push(USER_NOTIFICATION_SETTINGS_URL);
    onOpenChange(false);
  };

  const { header, actionWrapper, icon, content, dragHandle, button, markAsReadButton } = styles({
    isSmallScreen,
    hasUnread,
  });

  return (
    <DrawerAlt open={isOpen} direction={isSmallScreen ? 'bottom' : 'left'}>
      <DrawerAltPortal>
        <DrawerAltOverlay className="z-7" onClick={() => onOpenChange(false)} />
        <Drawer.Content className={content()} style={!isSmallScreen ? { left: offset } : undefined}>
          <VisuallyHidden>
            <Drawer.Title hidden>User notifications</Drawer.Title>
            <Drawer.Description>List of fluent cargo notifications</Drawer.Description>
          </VisuallyHidden>
          <div className={dragHandle()} />

          <div className={header()}>
            <Heading as="h4" variant="h4" className="m-0">
              Notifications
            </Heading>
            <div className={actionWrapper()}>
              {isMarkingAllAsRead ? (
                <LoadingIndicator className="ml-0 mr-4 px-0" />
              ) : (
                <button type="button" className={markAsReadButton()} onClick={handleMarkAllAsRead}>
                  <CheckCheck />
                  Mark all read
                </button>
              )}
              <Tooltip content="Notification settings">
                <button type="button" className={button()} onClick={handleClickSettings}>
                  <Settings className={icon()} />
                  <VisuallyHidden>Settings</VisuallyHidden>
                </button>
              </Tooltip>
              <Tooltip content="Close">
                <button type="button" className={button()} onClick={() => onOpenChange(false)}>
                  <X className={icon()} />
                  <VisuallyHidden>Close</VisuallyHidden>
                </button>
              </Tooltip>
            </div>
          </div>
          <div className="overflow-y-auto p-4">
            <UserNotificationsList
              notifications={notifications || []}
              isLoading={isLoading}
              error={error}
              shouldShowLoadMore={count && notifications ? count > notifications.length : false}
              onLoadMore={loadMore}
              onMarkAsRead={handleMarkAsRead}
            />
          </div>
        </Drawer.Content>
      </DrawerAltPortal>
    </DrawerAlt>
  );
};

const styles = tv({
  slots: {
    header: 'flex w-full justify-between p-5',
    actionWrapper: 'flex items-center gap-2',
    icon: 'size-4',
    button: 'grid size-7 place-items-center rounded-md text-text-secondary hover:bg-lightBlue-500 hover:text-white',
    markAsReadButton:
      'flex h-7 items-center gap-1 rounded-md px-1.5 text-sm text-grey-500 hover:bg-lightBlue-500 hover:text-white [&_svg]:size-4',
    content: 'z-7',
    dragHandle: 'mx-auto mt-4 h-2 w-[100px] rounded-full bg-grey-350 md:hidden',
  },
  variants: {
    isSmallScreen: {
      true: {
        content: 'fixed inset-x-0 bottom-0 top-0 z-50 mt-24 flex flex-col rounded-t-[10px] border bg-white',
      },
      false: {
        header: 'bg-grey-100',
        content: 'absolute bottom-0 flex h-full w-[480px] flex-col bg-white',
      },
    },
    hasUnread: {
      true: {
        markAsReadButton: 'text-lightBlue-600',
      },
      false: {},
    },
  },
});

export { AppNavigationNotificationsDrawer };
